import ApiUtil from './ApiUtil';

const ChartService = {
  rootUrl: '/api/v2',

  createChart(data, accessToken) {
    return ApiUtil.post(`${this.rootUrl}/charts`, data, accessToken);
  },

  updateChart(data, accessToken) {
    return ApiUtil.put(`${this.rootUrl}/charts`, data, accessToken);
  },

  fetchMultipleCharts(filter, accessToken) {
    return ApiUtil.get(`${this.rootUrl}/charts`, filter || {}, accessToken);
  },

  fetchChart(id, accessToken) {
    return ApiUtil.get(`${this.rootUrl}/charts/${id}`, null, accessToken);
  },

  fetchPrintChart(id) {
    return ApiUtil.get(`${this.rootUrl}/print/charts/${id}`, null, null);
  },

  findChartByGamsId(id, accessToken) {
    return ApiUtil.get(`${this.rootUrl}/charts/gams/${id}`, {}, accessToken);
  },

  findChartByMethodeId(id, accessToken) {
    return ApiUtil.get(`${this.rootUrl}/charts/methode/${id}`, {}, accessToken);
  },

  deleteChart(id, accessToken) {
    return ApiUtil.del(`${this.rootUrl}/charts/${id}`, null, accessToken);
  },

  publishImagesForImageManager(data, accessToken) {
    return ApiUtil.post(`${this.rootUrl}/publish/images`, data, accessToken);
  },

  publishImageManager(data, accessToken) {
    return ApiUtil.post(`${this.rootUrl}/publish/imageManager`, data, accessToken);
  },

  publishCLDImageManager(data, accessToken) {
    return ApiUtil.post(`${this.rootUrl}/publish/cldimageManager`, data, accessToken);
  },

  publishInset(data, accessToken) {
    return ApiUtil.post(`${this.rootUrl}/publish/inset`, data, accessToken);
  },

  publishHtml(data, accessToken) {
    return ApiUtil.post(`${this.rootUrl}/publish/html`, data, accessToken);
  },

  publishSlackNotification(payload, accessToken) {
    return ApiUtil.post(`${this.rootUrl}/publish/slackNotification`, { payload }, accessToken);
  },

  // proxy the download through the server
  async downloadPng(accessToken, publication, svg, chartCssPath, language) {
    const html = sanitizeSVG(svg);
    try {
      const response = await fetch(`${this.rootUrl}/download`, {
        method: 'post',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          pub_group: window.PUBLICATION_GROUP,
          'Content-Type': 'application/json',
          Accept: 'image/png',
        },
        body: JSON.stringify({
          html,
          publication,
          chartCssPath,
          language,
        }),
      });
      if (response.ok) {
        return response.blob();
      }
      throw new Error(response.statusText);
    } catch (error) {
      throw new Error(error);
    }
  },

  getSheet(spreadsheetId, query, accessToken) {
    return ApiUtil.get(
      `${this.rootUrl}/services/sheets?spreadsheetId=${spreadsheetId}${query}`,
      null,
      accessToken
    );
  },

  getInstrument(tickerName, accessToken) {
    return ApiUtil.get(
      `${this.rootUrl}/services/ticker?stockIndex=${tickerName}`,
      null,
      accessToken
    );
  },

  getTicker(
    tickerArr,
    frequency,
    start,
    end,
    dateRange,
    isPercent,
    product,
    isRolling,
    accessToken
  ) {
    const instrumentStr = tickerArr.reduce((str, item) => str + getSymbol(item), '');
    const format = isPercent ? 'percent' : 'value';
    const type = isRolling ? 'rolling' : 'fixed';
    const props = { frequency, start, end, dateRange, format, product, type };
    const propsStr = createPropsStr(props);
    return ApiUtil.get(
      `${this.rootUrl}/services/mike?${instrumentStr}${propsStr}`,
      null,
      accessToken
    );
  },

  executeDataDump(id, accessToken) {
    return ApiUtil.get(`${this.rootUrl}/charts/dump/${id}`, null, accessToken);
  },

  searchFred(searchText, accessToken) {
    return ApiUtil.get(
      `${this.rootUrl}/services/fred/series/search?search_text=${searchText}`,
      null,
      accessToken
    );
  },

  queryFred(
    ids,
    observation_start,
    observation_end,
    units,
    frequency,
    aggregation_method,
    accessToken
  ) {
    const props = { observation_start, observation_end, units, frequency, aggregation_method };
    const paramsStr = createParamsStr(ids, props);
    return ApiUtil.get(
      `${this.rootUrl}/services/fred/series/observations?${paramsStr}`,
      null,
      accessToken
    );
  },

  queryBLS(start, end, ids, calculationsType, accessToken) {
    const props = { start, end, calculationsType };
    const paramsStr = createParamsStr(ids, props);
    return ApiUtil.get(`${this.rootUrl}/services/bls?${paramsStr}`, null, accessToken);
  },
};

function sanitizeSVG(data) {
  return data
    .replace(/\u00a0/g, ' ')
    .replace(/\u0003/g, '')
    .replace('&nbsp;', ' ')
    .replace(/[\x00-\x1F\x7F-\x9F]/g, '');
}

function createParamsStr(ids, props) {
  const seriesStr = ids.reduce((str, id) => str + createParam('series_id', id), '');
  const propsStr = createPropsStr(props);
  return `${seriesStr}${propsStr}`;
}

function createPropsStr(props) {
  return Object.keys(props)
    .reduce((str, prop) => str + getProp(props, prop), '')
    .slice(0, -1);
}

function getSymbol(item) {
  const value = item.chartingSymbol.replace('&', '%26');
  return createParam('instrument', value);
}

function getProp(props, prop) {
  const value = (props[prop] || '').replace(/\+/g, '%2B');
  return createParam(prop, value);
}

function createParam(key, value) {
  return `${key}=${value}&`;
}

export default ChartService;
